import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileBarChart,
  DollarSign,
  BarChart3,
  ArrowUpRight,
  AlertTriangle,
  Info,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  LabelList,
} from 'recharts';
import { formatCompact } from '../lib/chartFormatters';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { convertAmount, currencyLabel } from '../lib/currencyConvert';
import { computeYtdSums } from '../lib/portfolioMetricUtils';
import { reportsAPI, transactionsAPI, portfolioAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { useForecast } from '../hooks/useForecast';
import { PortfolioMetricChart } from '../components/PortfolioMetricChart';
import { PayoutStateBadge } from '../components/PayoutStateBadge';
import { COST_BUCKET_LABELS } from '../lib/costTaxonomy';
import { businessLineSplit } from '../lib/revenueBusinessLines';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return `${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const revenueCategories = {
  revenue: 'Revenue',
  airbnb: 'Airbnb',
  booking_com: 'Booking.com',
  direct_booking: 'Direct Booking',
  booking_airbnb: 'Airbnb (Bookings)',
  booking_booking_com: 'Booking.com (Bookings)',
  booking_direct: 'Direct (Bookings)',
  booking: 'Other Bookings',
  other: 'Other',
};

const PIE_COLORS = [
  '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B',
  '#EC4899', '#06B6D4', '#EF4444', '#6366F1',
  '#14B8A6', '#F43F5E', '#64748B', '#EAB308',
];

const entityLabels = {
  // France
  afpv: 'AFPV Invest',
  bi_immo: 'Bonjour Immobilier',
  sci_113: 'SCI 113',
  pfv: 'PFV Holding',
  bi_sas: 'BI SAS',
  // UK
  br_ltd: 'BR Ltd',
  hsr: 'High Street Residence',
  eci: 'ECI',
  bi_ltd: 'BI Ltd',
  bi_uk: 'BI UK',
  bh: 'Bonjour Holding',
  // Personal
  fan_zhang: 'Fan Zhang',
  fan_zhang_piotr_kacki: 'FZ & PK',
  piotr_kacki: 'Piotr Kacki',
};

function SummaryCard({ label, value, icon: Icon, color, sub, change }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold font-mono', sub)}>{value}</p>
            {change !== null && change !== undefined && (
              <p className={cn(
                'text-xs font-medium mt-0.5',
                change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {change >= 0 ? '+' : ''}{change.toFixed(0)}% YoY
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, currency = 'GBP' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono">{formatAmount(entry.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

export function RevenuePageContent({ forceAllEntities = false } = {}) {
  // forceAllEntities: Overall P&L tabs show all business entities whatever
  // the scope picker holds (Fan, 2026-10-27).
  const entityStore = useEntityStore();
  const selectedEntity = forceAllEntities ? 'all' : entityStore.selectedEntity;
  const selectedScope = forceAllEntities ? 'entities' : entityStore.selectedScope;
  const { entities } = entityStore;
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const isPortfolio = selectedScope === 'portfolio';
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [priorYear, setPriorYear] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [revenueTransactions, setRevenueTransactions] = useState([]);
  const [portfolioData, setPortfolioData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [portfolioRegion, setPortfolioRegion] = useState('all');
  const [dqExpanded, setDqExpanded] = useState(false);
  // Revenues by business line (Property Portfolio / BR OS): the same entity
  // P&L the Profit & Loss tab reads, so the totals tie.
  const [linesPnl, setLinesPnl] = useState(null);
  const [priorLinesPnl, setPriorLinesPnl] = useState(null);

  // Forecast: rolling 90-day forward bookings for portfolio mode
  const { forecastByMonth } = useForecast({
    scope: 'portfolio', region: portfolioRegion, year,
  });
  const hasForecast = isPortfolio && forecastByMonth && Object.keys(forecastByMonth).length > 0;

  const resolveEntityId = () => {
    if (selectedEntity === 'all') return undefined;
    return selectedEntity;
  };

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      if (isPortfolio) {
        const data = await portfolioAPI.getMonthly({ year });
        setPortfolioData(data);
        setLinesPnl(null);
        setPriorLinesPnl(null);
        setReport(null);
        setPriorYear(null);
        setConsolidated(null);
        setRevenueTransactions([]);
      } else {
        setPortfolioData(null);
        const entityId = resolveEntityId();
        const promises = [
          reportsAPI.getMonthly({ year, entity_id: entityId }),
          reportsAPI.getMonthly({ year: year - 1, entity_id: entityId }),
          transactionsAPI.getAll({ entity_id: entityId, page_size: 200 }),
        ];
        if (selectedEntity === 'all') {
          promises.push(reportsAPI.getConsolidated({ year }));
        }
        // Same call as the Profit & Loss tab (ProfitLossPage fetchData).
        const pnlParams = (y) => (selectedEntity === 'all' ? { year: y, scope: 'entities' } : { year: y, entity_id: entityId });
        const linesPromise = reportsAPI.getEntityPnl(pnlParams(year));
        // Prior year on the same basis, for the Total Revenue card's YoY.
        const priorLinesPromise = reportsAPI.getEntityPnl(pnlParams(year - 1));

        const [results, linesResult, priorLinesResult] = await Promise.all([
          Promise.allSettled(promises),
          linesPromise.then((r) => r.data).catch(() => null),
          priorLinesPromise.then((r) => r.data).catch(() => null),
        ]);
        setLinesPnl(linesResult);
        setPriorLinesPnl(priorLinesResult);
        if (results[0].status === 'fulfilled') setReport(results[0].value.data);
        if (results[1].status === 'fulfilled') setPriorYear(results[1].value.data);
        if (results[2].status === 'fulfilled') {
          const txPayload = results[2].value.data;
          const txs = txPayload.transactions || [];
          setRevenueTransactions(txs.filter((t) => t.amount > 0));
        }
        if (results[3]?.status === 'fulfilled') setConsolidated(results[3].value.data);
        else setConsolidated(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all' || isPortfolio) {
      fetchData();
    }
  }, [year, selectedEntity, entities]);

  // Reset region tab when leaving portfolio mode
  useEffect(() => {
    if (!isPortfolio) setPortfolioRegion('all');
  }, [isPortfolio]);

  const summary = report?.summary;
  const priorSummary = priorYear?.summary;
  const reportCurrency = report?.currency || 'GBP';
  const fmt = (v) => formatAmount(v, reportCurrency);

  const yoyChange = (current, prior) => {
    if (!prior || prior === 0) return null;
    return ((current - prior) / Math.abs(prior)) * 100;
  };

  // ── Portfolio-mode derived data ──
  const eurGbpRate = parseFloat(portfolioData?.fx_rates_used?.EUR_GBP) || 0.85;
  const cv = (value, fromCurrency) => convertAmount(value, fromCurrency, displayCurrency, eurGbpRate);

  // Normalized monthly series for the shared chart component (native currencies)
  const revenueMonthlySeries = useMemo(() => {
    if (!portfolioData) return [];
    const now = new Date();
    return portfolioData.monthly
      .filter((m) => {
        const mn = parseInt(m.month.split('-')[1], 10);
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return mn <= now.getMonth() + 1;
      })
      .map((m) => ({
        month: MONTH_LABELS[parseInt(m.month.split('-')[1], 10) - 1].slice(0, 3),
        ukNative: m.uk_booking_revenue_gbp || 0,
        frNative: m.fr_booking_revenue_eur || 0,
      }));
  }, [portfolioData, year]);

  const portfolioChannelSources = useMemo(() => {
    if (!portfolioData) return [];
    const channels = {};
    const cvC = (v, from) => convertAmount(v, from, displayCurrency, eurGbpRate);
    const now = new Date();
    for (const m of portfolioData.monthly) {
      // Same actuals filter as revenueMonthlySeries and the monthly table
      const mn = parseInt(m.month.split('-')[1], 10);
      if (year === now.getFullYear() && mn > now.getMonth() + 1) continue;
      if (year > now.getFullYear()) continue;
      for (const b of (m.bookings || [])) {
        if (portfolioRegion !== 'all' && b.region !== portfolioRegion) continue;
        const ch = b.channel || 'Other';
        channels[ch] = (channels[ch] || 0) + cvC(b.net_revenue || 0, b.currency || (b.region === 'fr' ? 'EUR' : 'GBP'));
      }
    }
    return Object.entries(channels)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [portfolioData, portfolioRegion, displayCurrency, eurGbpRate, year]);

  const portfolioChannelTotal = portfolioChannelSources.reduce((s, c) => s + c.value, 0);

  // Monthly revenue chart data (entity mode)
  const chartData = useMemo(() => {
    if (!report) return [];
    const now = new Date();
    return report.months
      .filter((m) => {
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return m.month <= now.getMonth() + 1;
      })
      .map((m) => ({
        month: m.month_label.slice(0, 3),
        revenue: m.revenue,
        prior_revenue: priorYear?.months?.find((pm) => pm.month === m.month)?.revenue || 0,
      }));
  }, [report, priorYear, year]);

  // Revenue by source from transactions, grouped by currency
  const revenueSources = useMemo(() => {
    const sources = {};
    for (const tx of revenueTransactions) {
      const cat = tx.category || 'other';
      const label = revenueCategories[cat] || cat.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      if (!sources[label]) sources[label] = {};
      const cur = tx.currency || reportCurrency;
      sources[label][cur] = (sources[label][cur] || 0) + Math.abs(tx.amount);
    }
    return Object.entries(sources)
      .map(([name, byCurrency]) => ({
        name,
        byCurrency,
        value: Math.round(Object.values(byCurrency).reduce((s, v) => s + v, 0)),
      }))
      .sort((a, b) => b.value - a.value);
  }, [revenueTransactions, reportCurrency]);

  const totalRevenueSources = revenueSources.reduce((s, c) => s + c.value, 0);

  const totalRevenueByCurrency = useMemo(() => {
    const totals = {};
    for (const src of revenueSources) {
      for (const [cur, amt] of Object.entries(src.byCurrency)) {
        totals[cur] = (totals[cur] || 0) + amt;
      }
    }
    return totals;
  }, [revenueSources]);

  const fmtByCurrency = (byCurrency) => {
    const entries = Object.entries(byCurrency).filter(([, v]) => v > 0);
    if (entries.length === 0) return formatAmount(0, reportCurrency);
    if (entries.length === 1) return formatAmount(Math.round(entries[0][1]), entries[0][0]);
    return entries.map(([cur, amt]) => formatAmount(Math.round(amt), cur)).join(' / ');
  };

  const bestMonth = useMemo(() => {
    if (!report) return null;
    return report.months.reduce((best, m) => (m.revenue > (best?.revenue || 0) ? m : best), null);
  }, [report]);

  const avgMonthlyRevenue = summary ? summary.total_revenue / 12 : 0;

  const lines = useMemo(() => businessLineSplit(linesPnl, year), [linesPnl, year]);
  const linesFmt = (v) => formatAmount(v, lines?.currency || 'GBP');
  // Split the trend chart's current-year bar by business line when both exist
  // and the chart is in the P&L currency.
  const splitChart = !!(lines?.showSplit && (lines.currency || 'GBP') === reportCurrency);
  const trendData = useMemo(() => {
    if (!splitChart) return chartData;
    const byLabel = {};
    (lines.rows || []).forEach((r) => { byLabel[(r.label || '').slice(0, 3)] = r; });
    return chartData.map((d) => {
      const r = byLabel[d.month];
      const portfolio = r ? Math.round(r.portfolio * 100) / 100 : 0;
      const bros = r ? Math.round(r.bros * 100) / 100 : 0;
      return { ...d, portfolio, bros, split_total: Math.round((portfolio + bros) * 100) / 100 };
    });
  }, [splitChart, chartData, lines]);

  // Summary cards on the same P&L basis as the split bars (Fan, 2026-10-27),
  // so Total Revenue = sum of the bars and Best Month = the tallest bar.
  const priorLines = useMemo(() => businessLineSplit(priorLinesPnl, year - 1), [priorLinesPnl, year]);
  const pnlCards = useMemo(() => {
    if (!splitChart) return null;
    const rows = lines.rows || [];
    const total = lines.totals?.total || 0;
    const best = rows.reduce((b, r) => ((r.total || 0) > (b?.total || 0) ? r : b), null);
    const priorTotal = priorLines && (priorLines.currency || 'GBP') === (lines.currency || 'GBP')
      ? priorLines.totals?.total : null;
    return {
      total,
      average: rows.length ? total / rows.length : 0,
      best: best ? best.total : null,
      priorTotal,
    };
  }, [splitChart, lines, priorLines]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── PORTFOLIO MODE ──
  if (isPortfolio) {
    const ps = portfolioData?.summary;
    const nativeRegionCurrency = portfolioRegion === 'fr' ? 'EUR' : 'GBP';

    // Actuals: reuse the same basis as revenueMonthlySeries (current-or-past months only)
    const actualsYtd = computeYtdSums(revenueMonthlySeries);
    const totalRev = portfolioRegion === 'uk'
      ? cv(actualsYtd.uk, 'GBP')
      : portfolioRegion === 'fr'
      ? cv(actualsYtd.fr, 'EUR')
      : cv(actualsYtd.uk, 'GBP') + cv(actualsYtd.fr, 'EUR');

    // Actuals booking count from displayed months only (same filter as monthly table)
    const now = new Date();
    const actualsMonths = (portfolioData?.monthly || []).filter((m) => {
      const mn = parseInt(m.month.split('-')[1], 10);
      if (year < now.getFullYear()) return true;
      if (year > now.getFullYear()) return false;
      return mn <= now.getMonth() + 1;
    });
    const regionBookingCount = portfolioRegion === 'all'
      ? actualsMonths.reduce((s, m) => s + (m.booking_count || 0), 0)
      : actualsMonths.reduce((s, m) =>
          s + (m.bookings || []).filter((b) => b.region === portfolioRegion && !b.is_cancelled).length, 0);
    const actualsNights = portfolioRegion === 'all'
      ? actualsMonths.reduce((s, m) => s + (m.nights || 0), 0)
      : actualsMonths.reduce((s, m) =>
          s + (m.bookings || []).filter((b) => b.region === portfolioRegion).reduce((n, b) => n + (b.nights || 0), 0), 0);

    // Forward confirmed bookings (full-year floor minus actuals)
    const fullYearBookingCount = portfolioRegion === 'all'
      ? (ps?.booking_count || 0)
      : (portfolioData?.monthly || []).reduce((s, m) =>
          s + (m.bookings || []).filter((b) => b.region === portfolioRegion && !b.is_cancelled).length, 0);
    const fullYearRev = portfolioRegion === 'uk'
      ? cv(ps?.by_region?.uk?.booking_revenue ?? 0, 'GBP')
      : portfolioRegion === 'fr'
      ? cv(ps?.by_region?.fr?.booking_revenue ?? 0, 'EUR')
      : cv(ps?.by_region?.uk?.booking_revenue ?? 0, 'GBP') + cv(ps?.by_region?.fr?.booking_revenue ?? 0, 'EUR');
    const forwardBookingCount = fullYearBookingCount - regionBookingCount;
    const forwardRev = fullYearRev - totalRev;
    const subtitleText = portfolioRegion === 'uk'
      ? '\ud83c\uddec\ud83c\udde7 UK properties only'
      : portfolioRegion === 'fr'
      ? '\ud83c\uddeb\ud83c\uddf7 France properties only'
      : 'Property-level booking revenue across all portfolio properties';
    const dq = portfolioData?.data_quality || {};
    const dqTotal = (dq.missing_currency_count || 0) + (dq.missing_primary_amount_count || 0)
      + (dq.bookings_with_no_cost_data || 0) + (dq.region_entity_mismatches || 0)
      + (dq.smoobu_unexpected_currency || 0);

    return (
      <div className="flex flex-col gap-8" data-testid="revenue-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Net Booking Revenue - Property Portfolio</h1>
              <p className="text-muted-foreground mt-1">{subtitleText}</p>
            </div>
            <div className="flex items-center gap-3">
              <CurrencyToggle />
              <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={isRefreshing}>
                <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
                <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-semibold w-12 text-center">{year}</span>
                <button
                  onClick={() => setYear((y) => y + 1)}
                  disabled={year >= new Date().getFullYear()}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Scope banner */}
          <div className="p-2.5 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40">
            <div className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Net booking revenue from Uplisting + Smoobu properties only. Excludes entity-level invoices and transactions. Cross-currency totals use today's FX rate (approximate).
              </span>
            </div>
          </div>

          {/* Data quality banner */}
          {dqTotal > 0 && (() => {
            const issues = [];
            if (dq.region_entity_mismatches > 0) issues.push(`${dq.region_entity_mismatches} region/entity mismatch${dq.region_entity_mismatches === 1 ? '' : 'es'}`);
            if (dq.missing_currency_count > 0) issues.push(`${dq.missing_currency_count} missing currency`);
            if (dq.missing_primary_amount_count > 0) issues.push(`${dq.missing_primary_amount_count} missing amount${dq.missing_primary_amount_count === 1 ? '' : 's'}`);
            if (dq.bookings_with_no_cost_data > 0) issues.push(`${dq.bookings_with_no_cost_data} booking${dq.bookings_with_no_cost_data === 1 ? '' : 's'} with no cost data`);
            if (dq.smoobu_unexpected_currency > 0) issues.push(`${dq.smoobu_unexpected_currency} unexpected Smoobu currency`);
            const mismatchWarnings = (portfolioData?.warnings || []).filter((w) => w.type === 'region_entity_mismatch');
            return (
              <div className="p-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/40">
                <div className="flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-300">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{issues.join(', ')}</span>
                      {mismatchWarnings.length > 0 && (
                        <button
                          onClick={() => setDqExpanded((v) => !v)}
                          className="ml-2 p-0.5 rounded hover:bg-yellow-200/50 dark:hover:bg-yellow-800/50 transition-colors"
                        >
                          {dqExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                    {dq.region_entity_mismatches > 0 && (
                      <p className="mt-1 text-xs opacity-80">
                        A booking's property region does not match its owning entity's country, so its revenue may land in the wrong FR/UK total.
                      </p>
                    )}
                    {dqExpanded && mismatchWarnings.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs">
                        {mismatchWarnings.map((w, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="font-mono shrink-0">{'\u2022'}</span>
                            <span>
                              <span className="font-medium">{w.context?.property_name || w.context?.property_id}</span>
                              {' '}({w.context?.platform}, region {w.context?.expected_jurisdiction})
                              {' '}tagged to {w.context?.entity_jurisdiction} entity ({w.context?.entity_name || w.context?.entity_id})
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Region Tabs */}
          <Tabs value={portfolioRegion} onValueChange={setPortfolioRegion}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="fr">{'\ud83c\uddeb\ud83c\uddf7'} France</TabsTrigger>
              <TabsTrigger value="uk">{'\ud83c\uddec\ud83c\udde7'} UK</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Summary Cards + Revenue Chart + YTD sums (shared component) */}
          <PortfolioMetricChart
            cards={ps ? [
              {
                label: `Net Booking Revenue (${portfolioRegion === 'all' ? `${displayCurrency} approx` : currencyLabel(nativeRegionCurrency, displayCurrency)})`,
                value: totalRev != null ? formatAmount(totalRev, displayCurrency) : 'N/A',
                icon: TrendingUp,
                color: 'bg-green-100 dark:bg-green-900/30',
                sub: 'text-green-600 dark:text-green-400',
              },
              {
                label: 'Bookings',
                value: String(regionBookingCount),
                icon: BarChart3,
                color: 'bg-blue-100 dark:bg-blue-900/30',
                sub: 'text-blue-600 dark:text-blue-400',
              },
            ] : []}
            monthlySeries={revenueMonthlySeries}
            forecastByMonth={forecastByMonth}
            title="Net Booking Revenue Trend"
            ukLabel="UK Net"
            frLabel="FR Net"
            displayCurrency={displayCurrency}
            eurGbpRate={eurGbpRate}
            year={year}
            portfolioRegion={portfolioRegion}
            showForecastCaveat={hasForecast}
          />

          {/* Forward confirmed bookings (separate from actuals) */}
          {forwardBookingCount > 0 && (
            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Confirmed forward bookings ({forwardBookingCount} booking{forwardBookingCount !== 1 ? 's' : ''})
                </span>
                <span className="font-mono font-semibold text-muted-foreground">
                  {formatAmount(forwardRev, displayCurrency)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Full-year floor: {formatAmount(fullYearRev, displayCurrency)} ({fullYearBookingCount} bookings). Actuals + forward = floor.
              </p>
            </div>
          )}

          {/* Tabs: Monthly + Sources (no By Entity) */}
          <Tabs defaultValue="monthly">
            <TabsList>
              <TabsTrigger value="monthly">Monthly Breakdown</TabsTrigger>
              <TabsTrigger value="sources">Revenue Sources</TabsTrigger>
            </TabsList>

            {/* Portfolio Monthly Breakdown */}
            <TabsContent value="monthly" className="space-y-4">
              {portfolioData ? (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Month</TableHead>
                          {portfolioRegion === 'all' ? (
                            <>
                              <TableHead className="text-right">UK Net ({currencyLabel('GBP', displayCurrency)})</TableHead>
                              <TableHead className="text-right">FR Net ({currencyLabel('EUR', displayCurrency)})</TableHead>
                              <TableHead className="text-right">Combined ({displayCurrency} approx)</TableHead>
                            </>
                          ) : (
                            <>
                              <TableHead className="text-right">Net Revenue ({currencyLabel(nativeRegionCurrency, displayCurrency)})</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {portfolioData.monthly
                          .filter((m) => {
                            const mn = parseInt(m.month.split('-')[1], 10);
                            const now = new Date();
                            if (year < now.getFullYear()) return true;
                            if (year > now.getFullYear()) return false;
                            return mn <= now.getMonth() + 1;
                          })
                          .map((m) => {
                            const mn = parseInt(m.month.split('-')[1], 10);
                            const monthLabel = MONTH_LABELS[mn - 1];
                            const regionBookings = portfolioRegion === 'all'
                              ? (m.bookings || [])
                              : (m.bookings || []).filter((b) => b.region === portfolioRegion);
                            const regionBookingCt = regionBookings.length;
                            const regionNights = regionBookings.reduce((s, b) => s + (b.nights || 0), 0);
                            const hasData = regionBookingCt > 0;
                            const now = new Date();
                            const isCurrentMonth = mn === now.getMonth() + 1 && year === now.getFullYear();
                            const isExpanded = expandedMonths.has(mn);

                            const revForMonth = portfolioRegion === 'uk'
                              ? cv(m.uk_booking_revenue_gbp ?? 0, 'GBP')
                              : portfolioRegion === 'fr'
                              ? cv(m.fr_booking_revenue_eur ?? 0, 'EUR')
                              : cv(m.uk_booking_revenue_gbp ?? 0, 'GBP') + cv(m.fr_booking_revenue_eur ?? 0, 'EUR');
                            const toggleExpand = () => {
                              setExpandedMonths((prev) => {
                                const next = new Set(prev);
                                next.has(mn) ? next.delete(mn) : next.add(mn);
                                return next;
                              });
                            };
                            return (
                              <React.Fragment key={m.month}>
                                <TableRow
                                  className={cn(
                                    isCurrentMonth && 'bg-primary/5',
                                    hasData && 'cursor-pointer hover:bg-muted/50'
                                  )}
                                  onClick={hasData ? toggleExpand : undefined}
                                >
                                  <TableCell className="w-8 px-2">
                                    {hasData && (isExpanded
                                      ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                      : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {monthLabel}
                                    {isCurrentMonth && (
                                      <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                                    )}
                                    {hasData && (
                                      <span className="ml-2 text-xs text-muted-foreground">
                                        {regionBookingCt} booking{regionBookingCt !== 1 ? 's' : ''}, {regionNights} night{regionNights !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </TableCell>
                                  {portfolioRegion === 'all' ? (
                                    <>
                                      <TableCell className="text-right font-mono">
                                        {m.uk_booking_revenue_gbp > 0 ? formatAmount(cv(m.uk_booking_revenue_gbp, 'GBP'), displayCurrency) : '\u2014'}
                                      </TableCell>
                                      <TableCell className="text-right font-mono">
                                        {m.fr_booking_revenue_eur > 0 ? formatAmount(cv(m.fr_booking_revenue_eur, 'EUR'), displayCurrency) : '\u2014'}
                                      </TableCell>
                                      <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                        {revForMonth > 0 ? formatAmount(revForMonth, displayCurrency) : '\u2014'}
                                      </TableCell>
                                    </>
                                  ) : (
                                    <>
                                      <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                        {revForMonth > 0 ? formatAmount(revForMonth, displayCurrency) : '\u2014'}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                                {/* Expanded per-booking detail */}
                                {isExpanded && regionBookings.map((d, i) => (
                                  <TableRow key={`${m.month}-d-${i}`} className="bg-muted/20 text-xs">
                                    <TableCell></TableCell>
                                    <TableCell className="py-1.5" colSpan={portfolioRegion === 'all' ? 2 : 1}>
                                      <span className="font-medium">{d.property_name}</span>
                                      {d.guest_name && <span className="text-muted-foreground ml-2">{d.guest_name}</span>}
                                      <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{d.channel}</Badge>
                                      {d.management_type === 'managed' && (
                                        <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0">Managed</Badge>
                                      )}
                                      <span className="text-muted-foreground ml-2">{d.check_in} {'\u2192'} {d.check_out}</span>
                                      <span className="text-muted-foreground ml-1">({d.nights}n)</span>
                                      {d.invoice_status === 'invoiced' && (
                                        <Badge className="ml-2 text-[10px] px-1 py-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0">
                                          Invoiced{d.invoice_number ? ` ${d.invoice_number}` : ''}
                                        </Badge>
                                      )}
                                      {d.invoice_status === 'draft' && (
                                        <Badge className="ml-2 text-[10px] px-1 py-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0">Draft</Badge>
                                      )}
                                      {d.invoice_status === 'not_invoiced' && (
                                        <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 text-muted-foreground">Not invoiced</Badge>
                                      )}
                                      {d.invoice_status === 'cancelled' && (
                                        <Badge className="ml-2 text-[10px] px-1 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">Cancelled</Badge>
                                      )}
                                      {d.mgmt_statement_status === 'reconciled' && (
                                        <Badge className="ml-2 text-[10px] px-1 py-0 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0">Reconciled with mgmt statement</Badge>
                                      )}
                                      {d.mgmt_statement_status === 'not_reconciled' && (
                                        <Badge className="ml-2 text-[10px] px-1 py-0 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-0">Not on statement</Badge>
                                      )}
                                      {d.mgmt_statement_status === 'ref_matched_amount_mismatch' && (
                                        <Badge className="ml-2 text-[10px] px-1 py-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0">Statement amount mismatch</Badge>
                                      )}
                                      {d.mgmt_statement_status != null
                                        && !['reconciled', 'not_reconciled', 'ref_matched_amount_mismatch'].includes(d.mgmt_statement_status) && (
                                        <Badge
                                          variant="destructive"
                                          className="ml-2 text-[10px] px-1 py-0"
                                          title="The reconciliation resolver returned a status this UI has no label for -- surfacing the raw value rather than rendering no badge at all."
                                        >
                                          {d.mgmt_statement_status}
                                        </Badge>
                                      )}
                                      <span className="ml-2" title={d.match_tier ? `Match tier: ${d.match_tier}` : undefined}>
                                        <PayoutStateBadge state={d.payment_state} />
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-right font-mono py-1.5">
                                      {formatAmount(cv(d.net_revenue, d.currency), displayCurrency)}
                                    </TableCell>
                                    {portfolioRegion === 'all' && <TableCell></TableCell>}
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        {/* Totals row (actuals only, matches displayed month rows) */}
                        {ps && (
                          <TableRow className="border-t-2 font-semibold bg-muted/30">
                            <TableCell></TableCell>
                            <TableCell>Total ({regionBookingCount} bookings, {actualsNights} nights)</TableCell>
                            {portfolioRegion === 'all' ? (
                              <>
                                <TableCell className="text-right font-mono">
                                  {actualsYtd.uk > 0 ? formatAmount(cv(actualsYtd.uk, 'GBP'), displayCurrency) : '\u2014'}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {actualsYtd.fr > 0 ? formatAmount(cv(actualsYtd.fr, 'EUR'), displayCurrency) : '\u2014'}
                                </TableCell>
                                <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                  {totalRev > 0 ? formatAmount(totalRev, displayCurrency) : '\u2014'}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                  {totalRev > 0 ? formatAmount(totalRev, displayCurrency) : '\u2014'}
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
                    <p className="text-muted-foreground">No booking data available for the portfolio.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Portfolio Revenue Sources (channel breakdown) */}
            <TabsContent value="sources" className="space-y-4">
              {portfolioChannelSources.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Revenue by Channel</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={portfolioChannelSources}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={120}
                            dataKey="value"
                            paddingAngle={2}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            {portfolioChannelSources.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => formatAmount(value, displayCurrency)}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {portfolioChannelSources.map((src, i) => {
                          const pct = portfolioChannelTotal > 0
                            ? ((src.value / portfolioChannelTotal) * 100).toFixed(1) : '0.0';
                          return (
                            <div key={src.name} className="flex items-center gap-3">
                              <div
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between text-sm">
                                  <span className="font-medium truncate">{src.name}</span>
                                  <span className="font-mono text-muted-foreground ml-2">{pct}%</span>
                                </div>
                                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                                  />
                                </div>
                              </div>
                              <span className="font-mono text-sm font-medium text-right shrink-0">
                                {formatAmount(src.value, displayCurrency)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {portfolioChannelTotal > 0 && (
                        <div className="mt-4 pt-4 border-t flex justify-between text-sm font-semibold">
                          <span>Total</span>
                          <span className="font-mono">{formatAmount(portfolioChannelTotal, displayCurrency)}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No channel data</h3>
                    <p className="text-muted-foreground">Channel breakdown will appear once bookings are synced.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
      </div>
    );
  }

  // ── ENTITY MODE (unchanged) ──
  return (
    <div className="flex flex-col gap-8" data-testid="revenue-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Revenues</h1>
            <p className="text-muted-foreground mt-1">Income streams, trends, and growth analysis</p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
              <button
                onClick={() => setYear((y) => y - 1)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-semibold w-12 text-center">{year}</span>
              <button
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= new Date().getFullYear()}
                className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Data quality banner: surface quarantined/pending_review exclusions */}
        {(() => {
          const dq = (selectedEntity === 'all' ? consolidated?.data_quality : report?.data_quality) || {};
          const parts = [];
          if (dq.quarantined_bookings > 0)
            parts.push(`${dq.quarantined_bookings} quarantined booking${dq.quarantined_bookings === 1 ? '' : 's'}`);
          if (dq.quarantined_invoices > 0)
            parts.push(`${dq.quarantined_invoices} quarantined invoice${dq.quarantined_invoices === 1 ? '' : 's'}`);
          if (dq.pending_review_invoices > 0)
            parts.push(`${dq.pending_review_invoices} invoice${dq.pending_review_invoices === 1 ? '' : 's'} pending review`);
          const total = (dq.quarantined_bookings || 0) + (dq.quarantined_invoices || 0) + (dq.pending_review_invoices || 0);
          if (total === 0) return null;
          return (
            <div className="p-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/40">
              <div className="flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-300">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {total} record{total === 1 ? '' : 's'} excluded from this view: {parts.join(', ')}.
                </span>
              </div>
            </div>
          );
        })()}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              label="Total Revenue"
              value={fmt(pnlCards ? pnlCards.total : summary.total_revenue)}
              icon={TrendingUp}
              color="bg-green-100 dark:bg-green-900/30"
              sub="text-green-600 dark:text-green-400"
              change={pnlCards
                ? (pnlCards.priorTotal ? yoyChange(pnlCards.total, pnlCards.priorTotal) : null)
                : yoyChange(summary.total_revenue, priorSummary?.total_revenue)}
            />
            <SummaryCard
              label="Monthly Average"
              value={fmt(pnlCards ? pnlCards.average : avgMonthlyRevenue)}
              icon={BarChart3}
              color="bg-blue-100 dark:bg-blue-900/30"
              sub="text-blue-600 dark:text-blue-400"
            />
            <SummaryCard
              label="Best Month"
              value={pnlCards ? (pnlCards.best != null ? fmt(pnlCards.best) : '—') : (bestMonth ? fmt(bestMonth.revenue) : '—')}
              icon={ArrowUpRight}
              color="bg-emerald-100 dark:bg-emerald-900/30"
              sub="text-emerald-600 dark:text-emerald-400"
            />
            <SummaryCard
              label={summary.booking_entity_revenue > 0 ? 'Booking Revenue' : 'Gross Margin'}
              value={summary.booking_entity_revenue > 0
                ? fmt(summary.booking_entity_revenue)
                : summary.total_revenue > 0
                  ? `${((summary.gross_profit / summary.total_revenue) * 100).toFixed(1)}%`
                  : '—'}
              icon={DollarSign}
              color="bg-purple-100 dark:bg-purple-900/30"
              sub="text-purple-600 dark:text-purple-400"
            />
          </div>
        )}

        {/* Revenue Trend Chart: current-year bars split Property Portfolio / BR OS (Fan, 2026-10-27: "like a split bar") */}
        {report && chartData.some((d) => d.revenue > 0 || d.prior_revenue > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue Trend — {year} vs {year - 1}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={trendData} margin={{ top: 30, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} tickFormatter={(v) => `${CURRENCY_SYMBOLS[reportCurrency] || reportCurrency}${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTooltip currency={reportCurrency} />} />
                  <Legend />
                  {splitChart ? lines.lines.map((l, i) => (
                    <Bar key={l.key} dataKey={l.key} name={`${year} ${l.label}`} stackId="revenues" fill={l.color}
                      radius={i === lines.lines.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}>
                      {i === lines.lines.length - 1 && (
                        <LabelList dataKey="split_total" position="top" formatter={(v) => formatCompact(v, reportCurrency)} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                      )}
                    </Bar>
                  )) : (
                    <Bar dataKey="revenue" name={`${year} Revenue`} fill="#10B981" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="revenue" position="top" formatter={(v) => formatCompact(v, reportCurrency)} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                    </Bar>
                  )}
                  <Line
                    type="monotone"
                    dataKey="prior_revenue"
                    name={`${year - 1} Revenue`}
                    stroke="#94A3B8"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3, fill: '#94A3B8' }}
                  >
                    <LabelList dataKey="prior_revenue" position="bottom" formatter={(v) => formatCompact(v, reportCurrency)} style={{ fontSize: 11, fontWeight: 600, fill: '#94A3B8' }} offset={8} />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
              {splitChart && (
                <p className="text-xs text-muted-foreground mt-2">
                  Bars split by business line on the Profit &amp; Loss basis (BR OS invoiced, Property Portfolio gross bookings by check-in month).
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="monthly">
          <TabsList>
            {lines && <TabsTrigger value="lines">By Business Line</TabsTrigger>}
            <TabsTrigger value="monthly">Monthly Breakdown</TabsTrigger>
            <TabsTrigger value="sources">Revenue Sources</TabsTrigger>
            {selectedEntity === 'all' && (
              <TabsTrigger value="entity">By Entity</TabsTrigger>
            )}
          </TabsList>

          {lines && (
            <TabsContent value="lines" className="space-y-4">
              <Card>
                <CardContent className="p-0">
                  <Table data-testid="revenue-business-lines-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        {lines.lines.map((l) => (
                          <TableHead key={l.key} className="text-right">{l.label}</TableHead>
                        ))}
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.rows.map((r) => (
                        <TableRow key={r.month}>
                          <TableCell className="font-medium">{r.label}</TableCell>
                          {lines.lines.map((l) => (
                            <TableCell key={l.key} className="text-right font-mono">
                              {r[l.key] ? linesFmt(r[l.key]) : '\u2014'}
                            </TableCell>
                          ))}
                          <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                            {r.total ? linesFmt(r.total) : '\u2014'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="border-t-2 font-semibold bg-muted/30">
                        <TableCell>Total</TableCell>
                        {lines.lines.map((l) => (
                          <TableCell key={l.key} className="text-right font-mono">{linesFmt(lines.totals[l.key])}</TableCell>
                        ))}
                        <TableCell className="text-right font-mono text-green-600 dark:text-green-400">{linesFmt(lines.totals.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground">
                Same basis as the Profit & Loss tab: BR OS is invoiced revenue, Property Portfolio is gross booking revenue by check-in month. The current month counts as actual.
              </p>
            </TabsContent>
          )}

          {/* Monthly Breakdown Table */}
          <TabsContent value="monthly" className="space-y-4">
            {selectedEntity === 'all' && consolidated?.entity_months ? (() => {
              const em = consolidated.entity_months;
              const activeEntities = Object.keys(em);
              const now = new Date();
              const monthFilter = (m) => {
                if (year < now.getFullYear()) return true;
                if (year > now.getFullYear()) return false;
                return m.month <= now.getMonth() + 1;
              };
              const firstEntityMonths = em[activeEntities[0]] || [];
              const grandTotal = activeEntities.reduce(
                (s, id) => s + (em[id] || []).reduce((s2, m) => s2 + m.revenue, 0), 0
              );
              return (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-right">Total Revenue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {firstEntityMonths.filter(monthFilter).map((_, mi) => {
                          const mn = mi + 1;
                          const isCurrentMonth = mn === now.getMonth() + 1 && year === now.getFullYear();
                          const monthLabel = firstEntityMonths[mi].month_label;
                          const total = activeEntities.reduce((s, id) => s + (em[id]?.[mi]?.revenue || 0), 0);
                          const isExpanded = expandedMonths.has(mn);
                          const toggleExpand = () => {
                            setExpandedMonths((prev) => {
                              const next = new Set(prev);
                              next.has(mn) ? next.delete(mn) : next.add(mn);
                              return next;
                            });
                          };
                          const entityBreakdown = activeEntities
                            .map((id) => ({ id, label: entityLabels[id] || id, revenue: em[id]?.[mi]?.revenue || 0 }))
                            .filter((e) => e.revenue > 0)
                            .sort((a, b) => b.revenue - a.revenue);
                          return (
                            <React.Fragment key={mn}>
                              <TableRow
                                className={cn(
                                  isCurrentMonth && 'bg-primary/5',
                                  total > 0 && 'cursor-pointer hover:bg-muted/50'
                                )}
                                onClick={total > 0 ? toggleExpand : undefined}
                              >
                                <TableCell className="w-8 px-2">
                                  {total > 0 && (isExpanded
                                    ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                    : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {monthLabel}
                                  {isCurrentMonth && (
                                    <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                                  )}
                                  {entityBreakdown.length > 0 && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      {entityBreakdown.length} entit{entityBreakdown.length !== 1 ? 'ies' : 'y'}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                  {total > 0 ? fmt(total) : '—'}
                                </TableCell>
                              </TableRow>
                              {isExpanded && entityBreakdown.map((e) => (
                                <TableRow key={`${mn}-${e.id}`} className="bg-muted/20 text-sm">
                                  <TableCell></TableCell>
                                  <TableCell className="py-1.5 pl-8 font-medium">{e.label}</TableCell>
                                  <TableCell className="text-right font-mono py-1.5 text-green-600 dark:text-green-400">
                                    {fmt(e.revenue)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        <TableRow className="border-t-2 font-semibold bg-muted/30">
                          <TableCell></TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                            {fmt(grandTotal)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })() : report ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Gross Bookings</TableHead>
                        <TableHead className="text-right">{COST_BUCKET_LABELS.ota_commission}</TableHead>
                        <TableHead className="text-right">Net Bookings</TableHead>
                        <TableHead className="text-right">Total Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.months.filter((m) => {
                        if (year < new Date().getFullYear()) return true;
                        if (year > new Date().getFullYear()) return false;
                        return m.month <= new Date().getMonth() + 1;
                      }).map((m) => {
                        const hasBookings = m.booking_count > 0;
                        const isCurrentMonth = m.month === new Date().getMonth() + 1 && year === new Date().getFullYear();
                        const isExpanded = expandedMonths.has(m.month);
                        const toggleExpand = () => {
                          setExpandedMonths((prev) => {
                            const next = new Set(prev);
                            next.has(m.month) ? next.delete(m.month) : next.add(m.month);
                            return next;
                          });
                        };
                        return (
                          <React.Fragment key={m.month}>
                            <TableRow
                              className={cn(
                                isCurrentMonth && 'bg-primary/5',
                                hasBookings && 'cursor-pointer hover:bg-muted/50'
                              )}
                              onClick={hasBookings ? toggleExpand : undefined}
                            >
                              <TableCell className="w-8 px-2">
                                {hasBookings && (isExpanded
                                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {m.month_label}
                                {isCurrentMonth && (
                                  <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">
                                    Now
                                  </Badge>
                                )}
                                {hasBookings && (
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    {m.booking_count} booking{m.booking_count !== 1 ? 's' : ''}
                                    {m.booking_matched > 0 && (
                                      <Badge variant={m.booking_matched === m.booking_count ? 'default' : 'secondary'} className="ml-1 text-[10px] px-1 py-0">
                                        {m.booking_matched}/{m.booking_count} reconciled
                                      </Badge>
                                    )}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {hasBookings ? fmt(m.booking_gross) : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono text-red-500">
                                {hasBookings ? `-${fmt(m.booking_ota_commission)}` : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                                {hasBookings ? fmt(m.booking_net) : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                {m.revenue > 0 ? fmt(m.revenue) : '—'}
                              </TableCell>
                            </TableRow>
                            {/* Expanded booking details */}
                            {isExpanded && m.booking_details?.map((d, i) => (
                              <TableRow key={`${m.month}-d-${i}`} className="bg-muted/20 text-xs">
                                <TableCell></TableCell>
                                <TableCell className="py-1.5">
                                  <span className="font-medium">{d.property}</span>
                                  <span className="text-muted-foreground ml-2">{d.guest}</span>
                                  <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{d.channel}</Badge>
                                  {d.booking_source === 'bros' && (
                                    <Badge className="ml-1 text-[10px] px-1 py-0 bg-blue-100 text-blue-700 border-0">BR OS</Badge>
                                  )}
                                  <span className="text-muted-foreground ml-2">{d.check_in} → {d.check_out}</span>
                                  {d.management_type === 'managed' && (
                                    <Badge variant="secondary" className="ml-2 text-[10px] px-1 py-0">Managed</Badge>
                                  )}
                                  {d.payment_status === 'matched' && (
                                    <Badge className="ml-1 text-[10px] px-1 py-0 bg-green-100 text-green-700 border-0">Reconciled</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-mono py-1.5">
                                  {formatAmount(d.gross, d.currency)}
                                </TableCell>
                                <TableCell className="text-right font-mono py-1.5 text-red-500">
                                  {d.booking_source === 'bros'
                                    ? <span title="Supplier cost">-{formatAmount(d.supplier_cost, d.currency)}</span>
                                    : <>-{formatAmount(d.ota_commission, d.currency)}</>
                                  }
                                </TableCell>
                                <TableCell className="text-right font-mono py-1.5">
                                  {d.booking_source === 'bros'
                                    ? <span className="text-green-600" title="Margin">{formatAmount(d.margin, d.currency)}</span>
                                    : <>{formatAmount(d.net, d.currency)}
                                        {d.cleaning_fee > 0 && (
                                          <span className="text-muted-foreground ml-1" title="Cleaning fee">(+{formatAmount(d.cleaning_fee, d.currency)} clean)</span>
                                        )}
                                      </>
                                  }
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {/* Totals row */}
                      {summary && (
                        <TableRow className="border-t-2 font-semibold bg-muted/30">
                          <TableCell></TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right font-mono">
                            {summary.booking_gross > 0 ? fmt(summary.booking_gross) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-500">
                            {summary.booking_ota_commission > 0 ? `-${fmt(summary.booking_ota_commission)}` : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                            {summary.booking_net > 0 ? fmt(summary.booking_net) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                            {fmt(summary.total_revenue)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
                  <p className="text-muted-foreground">
                    Sync your integrations to populate revenue data.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Revenue Sources */}
          <TabsContent value="sources" className="space-y-4">
            {revenueSources.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Revenue by Source</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={revenueSources}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          dataKey="value"
                          paddingAngle={2}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {revenueSources.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => {
                            const src = revenueSources.find((s) => s.name === name);
                            return src ? fmtByCurrency(src.byCurrency) : fmt(value);
                          }}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {revenueSources.map((src, i) => {
                        const pct = totalRevenueSources > 0
                          ? ((src.value / totalRevenueSources) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <div key={src.name} className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between text-sm">
                                <span className="font-medium truncate">{src.name}</span>
                                <span className="font-mono text-muted-foreground ml-2">{pct}%</span>
                              </div>
                              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                                  }}
                                />
                              </div>
                            </div>
                            <span className="font-mono text-sm font-medium text-right shrink-0">
                              {fmtByCurrency(src.byCurrency)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {totalRevenueSources > 0 && (
                      <div className="mt-4 pt-4 border-t flex justify-between text-sm font-semibold">
                        <span>Total</span>
                        <span className="font-mono">{fmtByCurrency(totalRevenueByCurrency)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No revenue source data</h3>
                  <p className="text-muted-foreground">
                    Revenue sources will appear once transactions are synced.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* By Entity */}
          {selectedEntity === 'all' && (
            <TabsContent value="entity" className="space-y-4">
              {consolidated ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Revenue by Entity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(consolidated.entity_pnl)
                            .sort(([, a], [, b]) => b.revenue - a.revenue)
                            .map(([id, pnl]) => ({
                              entity: entityLabels[id] || id,
                              revenue: pnl.revenue,
                            }))}
                          margin={{ top: 30, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="entity" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${CURRENCY_SYMBOLS[reportCurrency] || reportCurrency}${(v / 1000).toFixed(0)}k`} />
                          <Tooltip content={<ChartTooltip currency={reportCurrency} />} />
                          <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="revenue" position="top" formatter={(v) => formatCompact(v, reportCurrency)} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Entity Revenue Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Entity</TableHead>
                            <TableHead className="text-right">Revenue</TableHead>
                            <TableHead className="text-right">% of Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(consolidated.entity_pnl)
                            .sort(([, a], [, b]) => b.revenue - a.revenue)
                            .map(([id, pnl]) => {
                              const totalRev = consolidated.consolidated.revenue_before_elimination;
                              const pct = totalRev > 0 ? ((pnl.revenue / totalRev) * 100).toFixed(1) : '0.0';
                              return (
                                <TableRow key={id}>
                                  <TableCell className="font-medium">{entityLabels[id] || id}</TableCell>
                                  <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                                    {fmt(pnl.revenue)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-muted-foreground">
                                    {pct}%
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          <TableRow className="border-t-2 font-semibold bg-muted/30">
                            <TableCell>Total</TableCell>
                            <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                              {fmt(consolidated.consolidated.revenue_after_elimination)}
                            </TableCell>
                            <TableCell className="text-right font-mono">100%</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading consolidated data...</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
    </div>
  );
}

export function RevenuePage() {
  return (
    <DashboardLayout>
      <RevenuePageContent />
    </DashboardLayout>
  );
}
